import React, { type SyntheticEvent, useRef, useState } from 'react';

import { Button, Tooltip } from '@mui/material';
import { FileUp, Info, MousePointer2 } from 'lucide-react';

import { useGetProjectById } from '../../hooks';
import { useGetDatasetById } from '../../hooks/tanstack/useDatasetsHook';
import { useGetIFS } from '../../hooks/tanstack/useIFSHook';
import {
  useGetRoles,
  useGetRouteParams,
  useHandleImportIFS,
} from '../../hooks/tanstack/useUtilsHook';
import { IFSTable } from '../IfsTable';
import LoadingDialog from '../LoadingDialog';
import {
  CustomTabPanel,
  Tab,
  Tabs,
} from '../projects/models/ProjectModelsManagement';
import SuccessDialog from '../SuccessDialog';

import AlertDialog from './AlertDialog';
import DownloadIFSTemplatesDialog from './DownloadIFSTemplatesDialog';
import IFSViewFilesDataTable from './IFSViewFilesDataTable';
import ImportExistingFilesDialog from './ImportExistingFilesDialog';

const TABS = ['Import', 'Data'];

const ProjectViewDataset = () => {
  const [index, setIndex] = useState(0);

  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openImportFilesDialog, setOpenImportFilesDialog] = useState(false);

  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const { projectRole } = useGetRoles();

  const { orgId, datasetId, projectId } = useGetRouteParams();

  const { importIfs, error, isPending } = useHandleImportIFS({
    inputFileRef,
    handleError: () => {
      setOpenErrorDialog(true);
    },
  });

  const { data: ifs } = useGetIFS(
    {
      projectId,
      datasetId,
      orgId,
    },
    {
      enabled: !!datasetId && !!orgId && !!projectId,
    }
  );

  const { data: dataset } = useGetDatasetById(
    {
      datasetId,
      projectId,
      orgId,
    },
    {
      enabled: !!datasetId && !!projectId && !!orgId,
    }
  );

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setIndex(newValue);
  };

  const handleOpenImportFilesDialog = () => {
    setOpenImportFilesDialog(true);
  };

  const handleCloseImportFilesDialog = () => {
    setOpenImportFilesDialog(false);
  };

  const isDisabled = !ifs?.files.some((file) => file.is_imported);
  const isAdmin = projectRole === 'project_owner' && !project?.is_guest_project;

  return (
    <div className="flex flex-col gap-4">
      <Tabs value={index} onChange={handleChange}>
        {TABS.map((report) => (
          <Tab
            id={report}
            key={report}
            label={report}
            disabled={report === 'Data' && !!isDisabled}
          />
        ))}
      </Tabs>
      <CustomTabPanel value={index.toString()} index="0">
        <div className="flex flex-col gap-3">
          <h1 className="text-2xl font-bold">Import</h1>
          <div className="flex gap-2 items-center">
            <div>
              <Info size={20} color="#666666" />
            </div>
            <p className=" text-[#666666] font-normal">
              If you update this dataset or import from an existing dataset, any
              models that use it will need to be re-run.
            </p>
          </div>
          <div className="flex justify-between">
            <div>
              {dataset && ifs && (
                <DownloadIFSTemplatesDialog
                  dataset={dataset}
                  ifs={ifs}
                  trigger={{
                    style: {
                      color: '#666',
                      borderColor: '#CCC',
                    },
                  }}
                />
              )}
            </div>
            <Tooltip
              title={isAdmin ? '' : 'Only project owners can import files'}
            >
              <div className="flex gap-4">
                <Button
                  variant="outlined"
                  disabled={!isAdmin}
                  style={{
                    display: 'flex',
                    position: 'relative',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <input
                    ref={inputFileRef}
                    type="file"
                    accept=".xlsx"
                    id="import_ifs"
                    onChange={importIfs}
                    multiple
                    className="absolute w-[140px] cursor-pointer opacity-0"
                  />
                  <FileUp size={20} />
                  Upload File(s)
                </Button>
                <Button
                  onClick={handleOpenImportFilesDialog}
                  variant="contained"
                  disabled={!isAdmin}
                  style={{
                    display: 'flex',
                    position: 'relative',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <MousePointer2 size={20} />
                  Import existing file(s)
                </Button>
              </div>
            </Tooltip>
          </div>
          <IFSTable />
          <ImportExistingFilesDialog
            open={openImportFilesDialog}
            handleClose={handleCloseImportFilesDialog}
          />
          <AlertDialog
            title="Error Found: Please Review"
            open={openErrorDialog}
            content={error?.content}
            handleOnClose={() => {
              setOpenErrorDialog(false);
            }}
          />
          <SuccessDialog
            content="Input File Set imported successfully."
            open={openSuccessDialog}
            handleOnClose={() => {
              setOpenSuccessDialog(false);
            }}
          />
          <LoadingDialog title="Uploading" open={isPending} />
        </div>
      </CustomTabPanel>
      <CustomTabPanel value={index.toString()} index="1">
        <div className="font-bold space-y-3">
          <h1 className="text-2xl font-bold">Data</h1>
          <IFSViewFilesDataTable />
        </div>
      </CustomTabPanel>
    </div>
  );
};

export default ProjectViewDataset;
